import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["input", "preview", "progress", "error"]
  static values = { 
    url: String,
    maxFiles: { type: Number, default: 0 }, // 0 means no limit
    maxSize: { type: Number, default: 5 } // MB
  }

  connect() {
    this.attachedFiles = []
    this.setupFileInput()
  }

  setupFileInput() {
    this.inputTarget.addEventListener('change', this.handleFileSelect.bind(this))
  }

  handleFileSelect(event) {
    const files = Array.from(event.target.files)
    
    // Validate files
    const validFiles = files.filter(file => this.validateFile(file))
    
    // For single file uploads (maxFiles = 1), replace existing file
    if (this.maxFilesValue === 1 && this.attachedFiles.length > 0 && validFiles.length > 0) {
      // Remove existing files first
      this.attachedFiles.forEach(attachedFile => {
        if (attachedFile.element) {
          attachedFile.element.remove()
        }
      })
      this.attachedFiles = []
      
      // Also remove any existing attachment previews from server-side renders
      const existingPreviews = this.previewTarget.querySelectorAll('.col-2')
      existingPreviews.forEach(preview => preview.remove())
    }
    
    // Check total file limit (for multi-file uploads)
    const totalFiles = this.attachedFiles.length + validFiles.length
    
    if (this.maxFilesValue != 0 && this.maxFilesValue !== 1 && totalFiles > this.maxFilesValue) {
      this.showError(`Možete priložiti maksimalno ${this.maxFilesValue} datoteka`)
      return
    }

    // Upload files
    validFiles.forEach(file => this.uploadFile(file))
    
    // Clear the input
    event.target.value = ''
  }

  validateFile(file) {
    // Check file size
    const maxSizeBytes = this.maxSizeValue * 1024 * 1024
    if (file.size > maxSizeBytes) {
      this.showError(`Datoteka "${file.name}" je prevelika. Maksimalna veličina je ${this.maxSizeValue}MB`)
      return false
    }

    // Check file type
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/gif',
      'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'video/mp4', 'video/mpeg', 'video/quicktime',
      'audio/mpeg', 'audio/mp3'
    ]

    console.log(file.type);
    
    
    if (!allowedTypes.includes(file.type)) {
      this.showError(`Tip datoteke "${file.name}" nije podržan`)
      return false
    }

    return true
  }

  uploadFile(file) {
    const upload = new DirectUpload(file, this.urlValue, this)
    
    // Create preview element
    const previewElement = this.createPreviewElement(file)
    this.previewTarget.appendChild(previewElement)
    
    // Start upload
    upload.create((error, blob) => {
      if (error) {
        this.handleUploadError(error, previewElement)
      } else {
        this.handleUploadSuccess(blob, previewElement, file)
      }
    })
  }

  createPreviewElement(file) {
    const element = document.createElement('div')
    element.className = 'col-2 position-relative'
    
    const isImage = file.type.startsWith('image/')
    const imageUrl = isImage ? URL.createObjectURL(file) : ''
    
    element.innerHTML = `
      <div class="img-responsive rounded-3 border ${isImage ? '' : 'bg-light d-flex align-items-center justify-content-center'}" 
           ${isImage ? `style="background-image: url(${imageUrl}); min-height: 80px;"` : 'style="min-height: 80px;"'}>
        ${isImage ? '' : this.getFileIcon(file)}
        <div class="position-absolute top-0 end-0 p-1 bg-light text-dark rounded-1">
          <button type="button" class="btn-close btn-sm" data-action="click->direct-upload#removeFile"></button>
        </div>
      </div>
      <div class="mt-1">
        <small class="text-muted d-block text-truncate">${file.name}</small>
        <div class="progress mt-1" style="height: 4px;">
          <div class="progress-bar" role="progressbar" style="width: 0%"></div>
        </div>
      </div>
    `
    return element
  }

  getFileIcon(file) {
    if (file.type === 'application/pdf') {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon text-danger mt-n60p">
        <path d="M14 3v4a1 1 0 0 0 1 1h4"></path>
        <path d="M5 12v-7a2 2 0 0 1 2 -2h7l5 5v4"></path>
        <path d="M5 18h1.5a1.5 1.5 0 0 0 0 -3h-1.5v6"></path>
        <path d="M17 18h2"></path>
        <path d="M20 15h-3v6"></path>
        <path d="M11 15v6h1a2 2 0 0 0 2 -2v-2a2 2 0 0 0 -2 -2h-1z"></path>
      </svg>`
    } else {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon text-muted mt-n60p">
        <path d="M14 3v4a1 1 0 0 0 1 1h4"></path>
        <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"></path>
      </svg>`
    }
  }

  handleUploadSuccess(blob, previewElement, file) {
    // Hide progress bar
    const progressBar = previewElement.querySelector('.progress')
    if (progressBar) progressBar.style.display = 'none'
    
    // Add hidden input with blob's signed_id
    const hiddenInput = document.createElement('input')
    hiddenInput.type = 'hidden'
    hiddenInput.name = this.inputTarget.name
    hiddenInput.value = blob.signed_id
    previewElement.appendChild(hiddenInput)
    
    // Store reference
    this.attachedFiles.push({
      element: previewElement,
      blob: blob,
      file: file
    })
    
    // Clear any error messages
    this.clearError()
  }

  handleUploadError(error, previewElement) {
    previewElement.classList.add('border-danger')
    this.showError(`Greška pri učitavanju: ${error.message}`)
  }

  removeFile(event) {
    const fileElement = event.target.closest('.col-2')
    if (fileElement) {
      // Remove from attachedFiles array
      this.attachedFiles = this.attachedFiles.filter(file => file.element !== fileElement)
      
      // Remove from DOM
      fileElement.remove()
    }
  }

  // DirectUpload delegate methods
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", event => {
      const progress = event.loaded / event.total * 100
      // Update progress bar - find the most recently added preview element
      const lastPreview = this.previewTarget.lastElementChild
      if (lastPreview) {
        const progressBar = lastPreview.querySelector('.progress-bar')
        if (progressBar) {
          progressBar.style.width = `${progress}%`
        }
      }
    })
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.style.display = 'block'
    }
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.style.display = 'none'
    }
  }
}
